import { Link } from 'react-router-dom';
import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { useAnimation } from '../../../hooks/useAnimation';

export default function BusinessIntro() {
  const heroSection = useAnimation({ threshold: 0.2 });
  const tableSection = useAnimation({ threshold: 0.2, delay: 100 });
  const imageSection = useAnimation({ threshold: 0.3, delay: 200 });
  const typeTableSection = useAnimation({ threshold: 0.3, delay: 100 });
  const feature1 = useAnimation({ threshold: 0.3, delay: 0 });
  const feature2 = useAnimation({ threshold: 0.3, delay: 150 });
  const feature3 = useAnimation({ threshold: 0.3, delay: 300 });
  const ctaSection = useAnimation({ threshold: 0.3, delay: 100 });

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Page Header */}
      <section className="relative h-[200px] md:h-[360px] flex items-center justify-center overflow-hidden mt-16 md:mt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/9898e9491df297fbf174f1c9f7508f75.jpg"
            alt="사업소개"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a2b5c]/80 via-[#1a2b5c]/70 to-[#1a2b5c]/80"></div>
        </div>
        <div className="relative z-10 text-center px-4 animate-[fadeIn_0.8s_ease-out]">
          <h1 className="text-[22px] md:text-[48px] font-bold text-white mb-1.5 md:mb-3" style={{ fontFamily: "'Noto Serif KR', serif" }}>
            사업소개
          </h1>
          <p className="text-[11px] md:text-[17px] text-white/85">
            가경 더 플래티넘 센트럴의 사업 개요를 소개합니다
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-2 md:py-4">
          <div className="flex items-center gap-2 text-[11px] md:text-[14px]">
            <Link to="/" className="text-gray-500 hover:text-[#1a2b5c] whitespace-nowrap">홈</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-500 whitespace-nowrap">사업안내</span>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-[#1a2b5c] font-medium whitespace-nowrap">사업소개</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section className="py-8 md:py-16">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">

          {/* 사업개요 타이틀 */}
          <div className="mb-6 md:mb-10">
            <div ref={heroSection.ref} className={`flex items-center gap-3 mb-2.5 md:mb-4 transition-all duration-700 ${heroSection.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              <div className="w-1 h-6 md:w-1.5 md:h-8 bg-[#1a2b5c] rounded-full"></div>
              <h2 className="text-[16px] md:text-[28px] font-bold text-[#1a2b5c]" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                사업개요
              </h2>
            </div>

            {/* 강조 문구 */}
            <div ref={heroSection.ref} className={`bg-gray-50 border-l-4 border-[#1a2b5c] rounded-r-xl px-3 md:px-6 py-3 md:py-5 mb-5 md:mb-8 transition-all duration-700 ${heroSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <p className="text-[10px] md:text-[15px] text-gray-700 mb-1 md:mb-2">
                SK하이닉스 신규 개발 및 고속터미널 개발 최대 수혜지,
              </p>
              <p className="text-[13px] md:text-[22px] font-bold text-[#1a2b5c] leading-snug">
                "548세대(O.P포함)의 랜드마크 브랜드 타운"
              </p>
              <div className="mt-1.5 md:mt-3 space-y-0.5 md:space-y-1">
                <p className="text-[10px] md:text-[14px] text-gray-600">
                  ▶ 지하 6층 ~ 지상 49층, 2개동 규모 / 공동주택 482세대 오피스텔 66실
                </p>
                <p className="text-[10px] md:text-[14px] text-gray-600">
                  ▶ 59타입 - 208세대, 84타입 - 274세대 / 선호도 높은 국민평형대 주력
                </p>
              </div>
            </div>

            {/* 이미지와 테이블 레이아웃 */}
            <div className="flex flex-col gap-5 md:gap-8 items-start mb-6 md:mb-10">
              {/* 건물 이미지 */}
              <div ref={imageSection.ref} className={`w-full transition-all duration-700 ${imageSection.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
                <div className="relative rounded-xl md:rounded-2xl overflow-hidden shadow-2xl border-4 border-[#1a2b5c]/20">
                  <img
                    src="https://static.readdy.ai/image/3b7ebc9e513f68fc027b047fbdd0fbe7/4a66f49681bd3fd03c601639b4c394ec.png"
                    alt="가경 더 플래티넘 센트럴 조감도"
                    className="w-full h-[260px] md:h-[680px] object-contain object-center bg-[#e8edf5]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a2b5c]/60 via-transparent to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 px-4 md:px-10 py-3 md:py-8">
                    <span className="inline-block bg-yellow-400 text-[#1a2b5c] text-[9px] md:text-[13px] font-bold px-2 py-0.5 rounded-full mb-1.5 md:mb-3 tracking-widest uppercase">조감도</span>
                    <p className="text-white text-[14px] md:text-[28px] font-bold drop-shadow-lg" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                      가경 더 플래티넘 센트럴
                    </p>
                    <p className="text-white/80 text-[10px] md:text-[15px] mt-0.5">충청북도 청주시 흥덕구 가경동 · 지하 6층 ~ 지상 49층 · 2개동</p>
                  </div>
                </div>
              </div>

              {/* 사업개요 테이블 */}
              <div ref={tableSection.ref} className={`w-full overflow-x-auto transition-all duration-700 ${tableSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                <table className="w-full border-collapse text-[10px] md:text-[14px]">
                  <thead>
                    <tr>
                      <th className="bg-[#1a2b5c] text-white text-center py-2 md:py-3 px-2 md:px-4 font-semibold w-[64px] md:w-[100px]">구분</th>
                      <th className="bg-[#1a2b5c] text-white text-center py-2 md:py-3 px-2 md:px-4 font-semibold">내용</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: '위치', value: '충청북도 청주시 흥덕구 가경동 1018번지 외 10' },
                      { label: '규모', value: '지하 6층 ~ 지상 49층(19층 피난층) / 2개동\n공동주택 482세대, 오피스텔 66실' },
                      { label: '대지면적', value: '6,479.90㎡ (1,960.17평)' },
                      { label: '건축면적', value: '4,365.84㎡ (1,320.67평)' },
                      { label: '연면적', value: '96,064.92㎡ (29,060평)' },
                      { label: '건폐율', value: '68.52%(법정 80% 이하)　용적률　999.96%(법정 1,000% 이하)' },
                      { label: '주차대수', value: '784대 (세대당 1.43대)' },
                    ].map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="border border-gray-200 text-center py-2 md:py-3 px-1.5 md:px-4 text-[#1a2b5c] font-medium">{row.label}</td>
                        <td className="border border-gray-200 py-2 md:py-3 px-1.5 md:px-4 text-gray-700 whitespace-pre-line leading-relaxed">{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 세대별 타입 테이블 */}
            <div ref={typeTableSection.ref} className={`overflow-x-auto rounded-xl shadow border border-gray-200 transition-all duration-700 ${typeTableSection.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <table className="w-full border-collapse text-[9px] md:text-[13px]">
                <thead>
                  <tr>
                    {['구분','타입','세대수','전용(m²)','전용(평)','공급(m²)','공급(평)','전용률'].map((head) => (
                      <th key={head} className="bg-[#1a2b5c] text-white text-center py-2 md:py-3 px-1 md:px-3 font-semibold border border-[#2d4a8f] whitespace-nowrap">
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { category: '공동주택', type: '59A', units: 92, exclusive_m2: '59.98', exclusive_p: '18.14', supply_m2: '87.44', supply_p: '26.45', ratio: '68.60%', rowspan: 5 },
                    { category: '', type: '59B', units: 92, exclusive_m2: '59.98', exclusive_p: '18.14', supply_m2: '87.44', supply_p: '26.45', ratio: '68.60%' },
                    { category: '', type: '59C', units: 24, exclusive_m2: '59.98', exclusive_p: '18.14', supply_m2: '87.44', supply_p: '26.45', ratio: '68.60%' },
                    { category: '', type: '84A', units: 91, exclusive_m2: '84.97', exclusive_p: '25.70', supply_m2: '123.87', supply_p: '37.47', ratio: '68.60%' },
                    { category: '', type: '84B', units: 92, exclusive_m2: '84.97', exclusive_p: '25.70', supply_m2: '123.87', supply_p: '37.47', ratio: '68.60%' },
                    { category: '', type: '84C', units: 91, exclusive_m2: '84.98', exclusive_p: '25.71', supply_m2: '123.88', supply_p: '37.47', ratio: '68.60%' },
                    { category: '오피스텔', type: 'O-59', units: 66, exclusive_m2: '59.98', exclusive_p: '18.14', supply_m2: '112.31', supply_p: '33.97', ratio: '53.41%' },
                    { category: '근린생활\n시설', type: '지상 1층', units: '-', exclusive_m2: '936.00', exclusive_p: '283.14', supply_m2: '1,912.86', supply_p: '578.64', ratio: '71.44%' },
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {row.category !== '' && (
                        <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-[#1a2b5c] font-medium whitespace-pre-line align-middle" rowSpan={row.rowspan}>
                          {row.category}
                        </td>
                      )}
                      <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-gray-700">{row.type}</td>
                      <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-gray-700">{row.units}</td>
                      <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-gray-700">{row.exclusive_m2}</td>
                      <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-gray-700">{row.exclusive_p}</td>
                      <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-gray-700">{row.supply_m2}</td>
                      <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-gray-700">{row.supply_p}</td>
                      <td className="border border-gray-200 text-center py-2 md:py-3 px-2 md:px-3 text-gray-700">{row.ratio}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 사업 특징 */}
          <div className="mt-8 md:mt-16 mb-8 md:mb-16">
            <div className="flex items-center gap-3 mb-5 md:mb-8">
              <div className="w-1 h-6 md:w-1.5 md:h-8 bg-[#1a2b5c] rounded-full"></div>
              <h2 className="text-[16px] md:text-[28px] font-bold text-[#1a2b5c]" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                사업 특징
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-6">
              {[
                { icon: 'ri-subway-line', title: '더블역세권 입지', desc: 'SK하이닉스 신규 개발 및 고속터미널 개발 최대 수혜지로 광역교통망 구축의 핵심위치입니다.', ref: feature1 },
                { icon: 'ri-shopping-bag-3-line', title: '프리미엄 상권', desc: '커넥트현대, 메가박스MX관, NC백화점, 롯데마트 등 원스톱 생활이 가능한 프리미엄 상권입니다.', ref: feature2 },
                { icon: 'ri-plant-line', title: '쾌적한 주거환경', desc: '루프탑 스카이라운지, 피트니스, 북카페 등 다양한 커뮤니티 시설로 품격 있는 생활을 누릴 수 있습니다.', ref: feature3 },
              ].map((item, i) => (
                <div key={i} ref={item.ref.ref} className={`bg-white rounded-xl p-4 md:p-7 shadow border border-gray-100 hover:shadow-lg transition-all duration-700 ${item.ref.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                  <div className="w-10 h-10 md:w-14 md:h-14 bg-[#1a2b5c] rounded-xl flex items-center justify-center mb-3 md:mb-5">
                    <i className={`${item.icon} text-white text-lg md:text-2xl`}></i>
                  </div>
                  <h3 className="text-[13px] md:text-[18px] font-bold text-[#1a2b5c] mb-1.5 md:mb-3">{item.title}</h3>
                  <p className="text-[11px] md:text-[14px] text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div ref={ctaSection.ref} className={`relative overflow-hidden rounded-2xl transition-all duration-700 ${ctaSection.isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Luxury%20premium%20residential%20apartment%20complex%20aerial%20view%20at%20dusk%2C%20modern%20high-rise%20towers%20with%20warm%20golden%20lights%2C%20sophisticated%20urban%20skyline%2C%20deep%20navy%20blue%20sky%20with%20subtle%20city%20glow%2C%20architectural%20photography%20style%2C%20cinematic%20atmosphere&width=1200&height=400&seq=business-cta-bg&orientation=landscape"
                alt="CTA 배경"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-br from-[#1a2b5c]/92 via-[#1a2b5c]/88 to-[#0f1a3d]/95"></div>
            </div>
            <div className="relative z-10 py-10 md:py-20 px-5 md:px-12 text-center">
              <span className="inline-block text-yellow-400 text-[10px] md:text-[13px] font-semibold tracking-[0.25em] uppercase mb-2 md:mb-4">
                CENTRAL CHEONGJU
              </span>
              <h2 className="text-[18px] md:text-[32px] font-bold text-white mb-2.5 md:mb-4" style={{ fontFamily: "'Noto Serif KR', serif" }}>
                가경 센트럴 더 플래티넘의 비전
              </h2>
              <p className="text-[11px] md:text-[16px] text-white/80 leading-relaxed max-w-[640px] mx-auto mb-6 md:mb-10">
                청주의 새로운 랜드마크로서 최상의 주거 가치를 제공하고,<br />
                입주민의 품격 있는 라이프스타일을 실현하는 프리미엄 주거 공간을 만들어갑니다.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2.5 md:gap-4 mb-6 md:mb-10">
                <Link
                  to="/reservation"
                  onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
                  className="inline-flex items-center gap-2 px-6 md:px-10 py-2.5 md:py-4 bg-yellow-400 text-[#1a2b5c] rounded-full text-[12px] md:text-[15px] font-bold hover:bg-yellow-300 transition-all whitespace-nowrap shadow-lg"
                >
                  <i className="ri-calendar-check-line"></i>
                  견본주택 방문 예약
                </Link>
                <a
                  href="tel:1555-5475"
                  onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
                  className="inline-flex items-center gap-2 px-6 md:px-10 py-2.5 md:py-4 bg-white/10 border border-white/30 text-white rounded-full text-[12px] md:text-[15px] font-semibold hover:bg-white/20 transition-all whitespace-nowrap"
                >
                  <i className="ri-phone-line"></i>
                  분양 문의 전화
                </a>
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-8 text-[10px] md:text-[13px] text-white/60">
                <span className="flex items-center gap-1.5">
                  <i className="ri-time-line text-yellow-400"></i>
                  상담 운영시간 10:00 – 18:00 (연중무휴)
                </span>
                <span className="hidden sm:block w-px h-4 bg-white/20"></span>
                <span className="flex items-center gap-1.5">
                  <i className="ri-map-pin-line text-yellow-400"></i>
                  충청북도 청주시 흥덕구 비하동 35-28
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 모바일 고정 플로팅 CTA 바 */}
      <div className="fixed bottom-0 left-0 right-0 z-[90] md:hidden">
        <div className="h-[2px] bg-gradient-to-r from-[#b8972a] via-[#f0d060] to-[#b8972a]"></div>
        <div className="bg-gradient-to-r from-[#0a1229] via-[#1a2b5c] to-[#0a1229] px-3 py-3 flex items-center gap-2.5 shadow-2xl">
          <Link
            to="/reservation"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group"
            style={{ background: 'linear-gradient(135deg, #b8972a 0%, #f0d060 40%, #c9a84c 70%, #b8972a 100%)' }}
          >
            <div className="absolute inset-0 bg-white/0 group-active:bg-white/10 transition-all"></div>
            <i className="ri-calendar-check-line text-[#0a1229] text-[18px]"></i>
            <span className="text-[14px] font-bold text-[#0a1229] relative z-10">방문예약</span>
          </Link>
          <a
            href="tel:1555-5475"
            onClick={() => { if (typeof (window as any).gtag_report_conversion === 'function') (window as any).gtag_report_conversion(); }}
            className="flex-1 flex items-center justify-center gap-2 py-3.5 bg-white rounded-xl whitespace-nowrap cursor-pointer relative overflow-hidden group border-2 border-white/30"
          >
            <div className="absolute inset-0 bg-[#1a2b5c]/0 group-active:bg-[#1a2b5c]/10 transition-all"></div>
            <i className="ri-phone-fill text-[#1a2b5c] text-[18px]"></i>
            <span className="text-[14px] font-bold text-[#1a2b5c] relative z-10">전화상담</span>
          </a>
        </div>
      </div>

      <Footer />
    </div>
  );
}
